import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';
import { DEFAULT_ROWS_PER_PAGE } from '../../utils/paginationUtils';

export const fetchArtists = createAsyncThunk('artists/list', (args) => {
  const params = args
    ? {
        search: args.search || '',
        page: args.page || 1,
        limit: args.limit || DEFAULT_ROWS_PER_PAGE,
      }
    : undefined;

  return api.get('/artists', { params }).then((response) => response.data);
});

export const createArtist = createAsyncThunk('artists/create', (args, { dispatch, getState }) => {
  const {
    first_name,
    last_name,
    description,
    description_en,
    description_de,
    description_hu,
    organizers_id,
    profile,
    index_for_search,
  } = args;
  const formData = new FormData();
  formData.append('first_name', first_name);
  formData.append('last_name', last_name);
  formData.append('description', description);
  formData.append('description_en', description_en);
  formData.append('description_de', description_de);
  formData.append('description_hu', description_hu);
  formData.append('organizers_id', organizers_id || '');
  formData.append('profile', profile);
  formData.append('index_for_search', index_for_search);
  return api.post('/artists', formData).then(() => dispatch(fetchArtists(getState().artists.options)));
});

export const updateArtist = createAsyncThunk('artists/update', (args, { dispatch, getState }) => {
  const {
    artistId,
    first_name,
    last_name,
    description,
    description_en,
    description_de,
    description_hu,
    organizers_id,
    profile,
    index_for_search,
  } = args;
  const formData = new FormData();
  formData.append('first_name', first_name);
  formData.append('last_name', last_name);
  formData.append('description', description);
  formData.append('description_en', description_en);
  formData.append('description_de', description_de);
  formData.append('description_hu', description_hu);
  formData.append('organizers_id', organizers_id || '');
  formData.append('profile', profile);
  formData.append('index_for_search', index_for_search);
  return api.patch(`/artists/${artistId}`, formData).then(() => dispatch(fetchArtists(getState().artists.options)));
});

const initialState = {
  data: [],
  totalItems: 0,
  error: null,
  isLoading: false,
  options: {
    search: '',
    page: 1,
    limit: DEFAULT_ROWS_PER_PAGE,
  },
};

const artistsSlice = createSlice({
  name: 'artists',
  initialState: initialState,
  reducers: {
    setFilters: (state, action) => {
      const { search, page, limit } = action.payload;
      if (search !== undefined) state.options.search = search;
      if (page !== undefined) state.options.page = page;
      if (limit !== undefined) state.options.limit = limit;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchArtists.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(fetchArtists.fulfilled, (state, action) => {
      state.isLoading = false;
      state.data = action.payload.data;
      state.totalItems = action.payload.totalItems ?? action.payload.data.length;
      state.options.page = action.payload.currentPage ?? state.options.page;
    });

    builder.addCase(fetchArtists.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(createArtist.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(createArtist.fulfilled, (state, action) => {
      state.isLoading = false;
    });

    builder.addCase(createArtist.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(updateArtist.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(updateArtist.fulfilled, (state, action) => {
      state.isLoading = false;
    });

    builder.addCase(updateArtist.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });
  },
});

export const { setFilters } = artistsSlice.actions;
export default artistsSlice.reducer;
